import React from 'react';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const Cookies: React.FC = () => {
  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 pb-8 pt-16">
        <div className="mx-auto max-w-4xl">
          <h1 className="mb-4 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-5xl font-bold text-transparent">
            Cookie Policy
          </h1>
          <p className="text-gray-400">Last updated: March 28, 2025</p>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 pb-16 pt-4">
        <div className="mx-auto max-w-4xl">
        <div className="space-y-12">
          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">What Are Cookies?</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They help websites remember your preferences and provide a better browsing experience.
            </p>
            <p className="text-gray-400 leading-relaxed">
              At kol.my, we use cookies and similar technologies to enhance your experience, analyze usage patterns, and deliver personalized content.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Types of Cookies We Use</h2>
            
            <div className="space-y-6">
              <div className="bg-white/5 border border-white/10 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3 text-white">Essential Cookies</h3>
                <p className="text-gray-400 mb-3 leading-relaxed">
                  These cookies are necessary for the platform to function properly. They enable core features like security, authentication, and accessibility.
                </p>
                <p className="text-sm text-gray-500">
                  <strong className="text-gray-300">Examples:</strong> Session management, load balancing, fraud prevention
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3 text-white">Performance Cookies</h3>
                <p className="text-gray-400 mb-3 leading-relaxed">
                  These cookies collect anonymous information about how visitors use our platform, helping us improve performance and user experience.
                </p>
                <p className="text-sm text-gray-500">
                  <strong className="text-gray-300">Examples:</strong> Page load times, error messages, most visited pages
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3 text-white">Functionality Cookies</h3>
                <p className="text-gray-400 mb-3 leading-relaxed">
                  These cookies remember your preferences and settings to provide a personalized experience.
                </p>
                <p className="text-sm text-gray-500">
                  <strong className="text-gray-300">Examples:</strong> Language preferences, timezone, dark mode
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3 text-white">Targeting & Advertising Cookies</h3>
                <p className="text-gray-400 mb-3 leading-relaxed">
                  These cookies track your browsing habits to deliver relevant advertisements and marketing content.
                </p>
                <p className="text-sm text-gray-500">
                  <strong className="text-gray-300">Examples:</strong> Personalized ads, campaign analytics, retargeting
                </p>
                <p className="text-sm text-violet-400 mt-2">
                  These cookies require explicit consent
                </p>
              </div>

              <div className="bg-white/5 border border-white/10 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-3 text-white">Third-Party Cookies</h3>
                <p className="text-gray-400 mb-3 leading-relaxed">
                  Some cookies are placed by third-party services that appear on our platform, such as analytics and social media integrations.
                </p>
                <p className="text-sm text-gray-500">
                  <strong className="text-gray-300">Examples:</strong> Google Analytics, Facebook Pixel, LinkedIn Insights
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">How Long Do Cookies Last?</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li><strong className="text-white">Session Cookies:</strong> Deleted when you close your browser</li>
              <li><strong className="text-white">Persistent Cookies:</strong> Remain on your device for a set period (days, months, or years)</li>
              <li><strong className="text-white">Secure Cookies:</strong> Only transmitted over HTTPS connections</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Managing Your Cookie Preferences</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              You have several options for managing cookies:
            </p>
            <div className="space-y-4">
              <div className="bg-violet-500/10 border border-violet-500/20 p-4 rounded-lg">
                <h4 className="font-bold text-white mb-2">Browser Settings</h4>
                <p className="text-gray-400 text-sm">
                  Most browsers allow you to block or delete cookies through their settings. However, this may affect your experience on our platform.
                </p>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-lg">
                <h4 className="font-bold text-white mb-2">Cookie Consent Banner</h4>
                <p className="text-gray-400 text-sm">
                  When you first visit our site, you can accept or reject non-essential cookies through our consent banner.
                </p>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-lg">
                <h4 className="font-bold text-white mb-2">Preference Center</h4>
                <p className="text-gray-400 text-sm">
                  Update your preferences at any time through our Cookie Settings page.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Impact of Cookie Choices</h2>
            <div className="bg-yellow-500/10 border-l-4 border-yellow-500/50 p-4 rounded-r-lg">
              <p className="text-yellow-200">
                <strong>Important:</strong> Disabling certain cookies may limit functionality or prevent you from using specific features of the kol.my platform.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Updates to This Policy</h2>
            <p className="text-gray-400 leading-relaxed">
              We may update this Cookie Policy periodically to reflect changes in our practices or for legal, operational, or regulatory reasons. Please revisit this page regularly to stay informed about our use of cookies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">Contact Us</h2>
            <p className="text-gray-400 leading-relaxed">
              For questions about our use of cookies, contact us at:<br />
              <strong className="text-white">Email:</strong> i@kol.my<br />
              <strong className="text-white">Phone:</strong> +60 3-1234 5678
            </p>
          </section>
        </div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default Cookies;
